import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Flame, FastForward, ArrowRight, Car, CalendarClock } from "lucide-react";
import { FORMULES_TABS, FORMULES_EXTRA, SITE } from "../../data/site";
import { Reveal, SectionHeading } from "./Reveal";
import { scrollToId } from "../../lib/scroll";
import { handleSpotlight } from "../../lib/spotlight";

const TABS = [
  { key: "classique", label: "Classique", icon: null },
  { key: "accelere", label: "Accéléré", icon: FastForward },
];

const FormuleCard = ({ f, delay }) => (
  <Reveal delay={delay} className="h-full">
    <motion.article
      whileHover={{ y: -8 }}
      transition={{ duration: 0.25 }}
      onMouseMove={handleSpotlight}
      data-testid={`formule-card-${f.id}`}
      className={`spotlight relative h-full rounded-2xl p-8 sm:p-9 flex flex-col border ${
        f.featured
          ? "beam bg-[#F3E8FF] border-violet-300 shadow-[0_20px_60px_rgb(109,40,217,0.14)] ring-1 ring-violet-300"
          : "bg-white border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="uppercase tracking-[0.18em] text-xs font-bold text-slate-400">Nos formules</span>
          <h3 className="font-heading font-bold text-slate-900 text-lg sm:text-xl mt-2 text-center sm:text-left">{f.transmission}</h3>
        </div>
        <span
          data-testid={`formule-popular-${f.id}`}
          className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[11px] font-bold uppercase tracking-wider px-3.5 py-1.5 shadow-lg shadow-violet-500/25"
        >
          <Flame size={12} /> Populaire
        </span>
      </div>
      <p className="font-heading font-black text-5xl text-slate-900 mt-6">
        {f.price}
        <span className="text-sm font-semibold text-slate-400 ml-2">TTC</span>
      </p>
      <div className={`h-px my-7 ${f.featured ? "bg-violet-200" : "bg-slate-100"}`} />
      <ul className="space-y-3.5 flex-1">
        {f.features.map((feat) => (
          <li key={feat} className="flex items-center gap-3 text-sm font-semibold text-slate-600">
            <span className={`w-5.5 h-5.5 w-[22px] h-[22px] rounded-full flex items-center justify-center shrink-0 ${f.featured ? "bg-violet-700" : "bg-violet-100"}`}>
              <Check size={12} className={f.featured ? "text-white" : "text-violet-700"} strokeWidth={3} />
            </span>
            {feat}
          </li>
        ))}
      </ul>
      <button
        data-testid={`formule-cta-${f.id}`}
        onClick={() => scrollToId("#contact")}
        className={`btn-shine group mt-8 w-full rounded-full font-bold py-3.5 flex items-center justify-center gap-2 text-sm transition-[background-color,color] duration-200 ${
          f.featured
            ? "bg-violet-700 text-white hover:bg-violet-800"
            : "bg-slate-100 text-slate-800 hover:bg-violet-700 hover:text-white"
        }`}
      >
        Choisir cette formule
        <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
      </button>
    </motion.article>
  </Reveal>
);

export const Formules = () => {
  const [tab, setTab] = useState("classique");

  return (
    <section id="formules" data-testid="formules-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="glow-blob absolute top-40 -right-40 w-[500px] h-[500px] rounded-full bg-violet-200/45" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading
          label="Nos formules"
          title="Des tarifs clairs, des formations complètes"
          description="Formation classique à votre rythme ou accélérée en intensif, en boîte manuelle ou automatique : choisissez la formule adaptée à votre projet. Financement CPF et permis à 1 € par jour acceptés."
        />

        <Reveal delay={0.1}>
          <div className="flex justify-center mt-10" data-testid="formules-tabs">
            <div className="inline-flex items-center rounded-full bg-white border border-slate-200 p-1.5 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
              {TABS.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  data-testid={`formules-tab-${key}`}
                  onClick={() => setTab(key)}
                  className={`relative flex items-center gap-2 rounded-full px-7 py-2.5 text-sm font-bold transition-colors duration-200 ${
                    tab === key ? "text-white" : "text-slate-500 hover:text-violet-700"
                  }`}
                >
                  {tab === key && (
                    <motion.span
                      layoutId="formules-tab-pill"
                      className="absolute inset-0 rounded-full bg-violet-700"
                      transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    />
                  )}
                  <span className="relative flex items-center gap-2">
                    {Icon && <Icon size={15} />}
                    {label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className={`grid gap-6 mx-auto mt-12 ${
              FORMULES_TABS[tab].length === 3 ? "md:grid-cols-3 max-w-6xl" : "md:grid-cols-2 max-w-4xl"
            }`}
            data-testid={`formules-grid-${tab}`}
          >
            {FORMULES_TABS[tab].map((f, i) => (
              <FormuleCard key={f.id} f={f} delay={i * 0.08} />
            ))}
          </motion.div>
        </AnimatePresence>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mt-6">
          {FORMULES_EXTRA.map((extra, i) => (
            <Reveal key={extra.id} delay={i * 0.08} className="h-full">
              <motion.article
                whileHover={{ y: -6 }}
                transition={{ duration: 0.25 }}
                onMouseMove={handleSpotlight}
                data-testid={`formule-extra-${extra.id}`}
                className="spotlight h-full rounded-2xl bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-7 sm:p-8"
              >
                <h3 className="font-heading font-bold text-slate-900 text-lg sm:text-xl text-center sm:text-left">{extra.title}</h3>
                {extra.subtitle && (
                  <p className="text-sm font-semibold text-slate-500 mt-2">{extra.subtitle}</p>
                )}
                <p className="text-sm text-slate-500 leading-relaxed mt-2">{extra.description}</p>
                <div className="mt-6 space-y-3">
                  {extra.options.map((opt) => (
                    <p
                      key={opt}
                      className="rounded-xl bg-violet-50 border border-violet-100 px-4 py-3 text-sm font-bold text-violet-700"
                    >
                      {opt}
                    </p>
                  ))}
                </div>
              </motion.article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div className="max-w-4xl mx-auto mt-10 text-center" data-testid="formules-notes">
            <div className="inline-flex items-center gap-3 rounded-full bg-white border border-slate-200 px-6 py-3.5 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
              <Car size={18} className="text-slate-700" />
              <span className="text-sm font-semibold text-slate-700">Heure d'évaluation : <strong className="text-slate-900">55 €</strong></span>
            </div>
            <p className="flex items-center justify-center gap-2 text-sm text-slate-500 mt-5">
              <CalendarClock size={15} className="text-violet-600" />
              Tous nos forfaits sont valables un an à compter de la date d'achat.
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Une question ? Écrivez-nous à{" "}
              <a href={`mailto:${SITE.email}`} className="font-bold text-violet-700 hover:underline">
                {SITE.email}
              </a>
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
